/**
 * Barrel for the static internal-GIS-layer catalog. Source JSON is
 * imported here, normalized
 * to the runtime `InternalGisLayer` shape, and concatenated into a
 * single readonly array for the `// add` action to consume.
 *
 * The current source file is `layers.json`, which intentionally does
 * not require category metadata because these rows are loaded as
 * session-scoped on-demand layers.
 */
import layers from './layers.json';

/**
 * Runtime shape after normalization. Mirrors the JSON entry shape
 * but keeps a single canonical name for the URL field (`url`) so
 * downstream consumers don't have to know about the JSON's
 * `gis_service_url` key.
 */
export interface InternalGisLayer {
  /** Service-side display name (used as the row label). */
  name: string;
  /** ArcGIS / WMS / similar service URL. */
  url: string;
  /** Optional source category for legacy catalogs; absent in layers.json. */
  category?: string;
  /** Optional row description for richer add-suggestion context. */
  description?: string;
  /** Optional keywords surfaced as suggestion pills. */
  tags?: string[];
}

interface RawJsonEntry {
  name: string;
  gis_service_url: string;
  category?: string;
  description?: string;
  tags?: string[];
}

function normalize(entries: readonly RawJsonEntry[]): InternalGisLayer[] {
  return entries.map(e => ({
    name: e.name,
    url: e.gis_service_url,
    category: e.category,
    description: e.description,
    tags: e.tags,
  }));
}

/**
 * Flattened, runtime-typed catalog of every internal GIS layer
 * available to the `// add` action.
 */
export const INTERNAL_GIS_LAYERS: readonly InternalGisLayer[] = [
  ...normalize(layers as RawJsonEntry[]),
];
